import React from 'react';
import { Box } from '@mui/material';
import { RowLayoutProps } from './RowLayout.props';

/**
 * RowLayout - Simple horizontal flexbox layout component
 *
 * @example
 * ```tsx
 * <RowLayout spacing="md" alignItems="center">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </RowLayout>
 * ```
 */
export const RowLayout: React.FC<RowLayoutProps> = ({
  children,
  spacing,
  alignItems,
  justifyContent,
  wrap,
  fullHeight,
  fullWidth,
  sx,
  ...boxProps
}) => {
  // Convert spacing name to MUI theme spacing multiplier
  const getSpacingMultiplier = (
    spacingValue: RowLayoutProps['spacing']
  ): number | undefined => {
    if (spacingValue === undefined) return undefined;
    if (typeof spacingValue === 'number') return spacingValue;

    switch (spacingValue) {
      case 'none':
        return 0;
      case 'xs':
        return 0.5;
      case 'sm':
        return 1;
      case 'md':
        return 2;
      case 'lg':
        return 3;
      case 'xl':
        return 4;
      case '2xl':
        return 6;
      default:
        return undefined;
    }
  };

  const spacingMultiplier = getSpacingMultiplier(spacing);

  return (
    <Box
      {...boxProps}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        ...(alignItems && { alignItems }),
        ...(justifyContent && { justifyContent }),
        ...(spacingMultiplier !== undefined && { gap: spacingMultiplier }),
        ...(wrap !== undefined && { flexWrap: wrap ? 'wrap' : 'nowrap' }),
        ...(fullWidth && { width: '100%' }),
        ...(fullHeight && { height: '100%' }),
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
